import { useEffect, useState } from 'react';

/**
 * Google's webfonts API keys each font file by variant name: '100', '300', 'regular', '500',
 * 'italic', '500italic', '900italic'…
 *
 * These must be translated into a valid `font-weight` + `font-style` pair. Emitting the raw key
 * as the weight (`font-weight: 900italic`) is invalid CSS: the browser discards the descriptor and
 * falls back to weight 400 / style normal, so every italic file ends up registered as the regular
 * face — and because Google lists italics last, the heaviest italic silently overrode the real
 * regular face for the whole family.
 *
 * Kept in sync with the customer app's copy at app/Components/CustomHooks/FontHook.js so the
 * preview renders what the shop will render.
 */
const parseVariant = (variant) => {
    const italic = /italic$/i.test(String(variant))
    const weight = String(variant).replace(/italic$/i, '')
    return {
        italic,
        // 'regular' and a bare 'italic' both mean weight 400
        weight: /^[1-9]\d{2}$/.test(weight) ? Number(weight) : 400,
    }
}

const useFontLoader = (fontData, family) => {
    const [fontCss, setFontCss] = useState(null);
    useEffect(() => {
        if (!fontData || !family) {
            setFontCss(null);
            return;
        }
        const css = Object.entries(fontData)
            .map(([variant, url]) => {
                const { italic, weight } = parseVariant(variant)
                return `
          @font-face {
            font-family: '${family}';
            font-style: ${italic ? 'italic' : 'normal'};
            font-weight: ${weight};
            font-display: swap;
            src: url(${url}) format('truetype');
          }
        `
            })
            .join('');
        setFontCss(css);
    }, [fontData, family]);
    return fontCss;
};
export default useFontLoader;
